/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Polygon from '@arcgis/core/geometry/Polygon';
import isNil from 'lodash.isnil';
import {useRouter} from 'next/compat/router';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getAllAccountIds} from '@ion/core/src/services/FetchWorkerUtils';
import {CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {isFalse} from '@ion/core/src/utils/BooleanUtils';
import {
  ALL_FEATURES_DEF,
  EXTENT_FACTOR,
  mapViewGoToOptions
} from '@ion/dls/src/maps/constants';
import {zoomToClusterExtent} from '@ion/dls/src/maps/helpers/MapViewUtils';
import FleetAssetLayer from '@ion/dls/src/maps/layers/FleetAssetLayer';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {TrackingMode} from '@ion/dls/src/types/types';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {useLoadAssets} from '@ion/query/src/hooks/useLoadAssets';
import {useLoadAssetUpdatesV2} from '@ion/query/src/hooks/useLoadAssetUpdatesV2';
import {Route} from '@/routes/RouteDefinitions';
import {getItemValue} from '@/routes/tracking/trackingUtils';
import {
  getFleetDefinitionExpression,
  getValuebyField,
  toLiveHistoryExclusion
} from '@/routes/tracking2/helpers';
import useFilteredAssetIds from '@/routes/tracking2/hooks/grid/useFilteredAssetIds';
import useFleetAssetPopup from '@/routes/tracking2/hooks/popup/useFleetAssetPopup';
import {
  setFindNearbyRefresh,
  setFMAssetInitialized,
  setSelectedAsset,
  updateSelectedAsset
} from '@/store/actions/tracking';
import {isCurrentPage} from '@/store/helpers';
import {
  globalGroupIdSelector,
  globalGroupStateSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  assetLabelContentSelector,
  assetsUpdatesSelector,
  findNearbyV2IsActiveSelector,
  isClusteringV2ActiveSelector,
  isFMAssetInitializedSelector,
  isLabellingActiveSelector,
  isMapSelectionSelector,
  liveHistoryActiveSelector,
  liveHistoryAssetSearchListSelector,
  liveHistoryDisplayReportSelector,
  mapBasemapSelector,
  mapLayerSelector,
  mapSelectedAssetSelector,
  selectedAssetSelector,
  trackingFiltersSelector,
  trackingGridStateSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {getLastAVLEvent, mergeNodesIntoRoutes} from '../../../helpers';
import {useTrackedFleetUpdate} from '../../../hooks/useTrackedFleetUpdate';
import useViewExtent from '../hooks/useViewExtent';
import {createAssetExtentTest} from './assetExtentUtils';

const MIN_ZOOM_LEVEL_FOR_ASSET_ANIMATION = 12;

interface FleetAssetLayerContainerProps extends MapContainerProps {
  isFleetAssetLayerRendered?: Boolean;
  setFleetAssetLayerRendered?: Function;
};

const FleetLayerContainer = ({
  map,
  view,
  clusterColor,
  isFleetAssetLayerRendered,
  setFleetAssetLayerRendered
}: FleetAssetLayerContainerProps, ref) => {
  const isMounted                               = useHasMounted(null);
  const dispatch                                = useDispatch();
  const route                                   = useRouter();
  const [selectedAssetMap, setSelectedAssetMap] = useState(null);
  const [isLayerReady, setLayerReady]           = useState(false);
  const [isLayerEnabled, setLayerEnabled]       = useState(false);
  const fleetAssetLayerRef                      = useRef<GenericObject>();
  const showPopup                               = useFleetAssetPopup(view);
  const assetsUpdates                           = useSelector(assetsUpdatesSelector);
  const selectedAsset                           = useSelector(selectedAssetSelector);
  const mapSelectedAsset                        = useSelector(mapSelectedAssetSelector);
  const labelContent                            = useSelector(assetLabelContentSelector);
  const basemap                                 = useSelector(mapBasemapSelector);
  const layers                                  = useSelector(mapLayerSelector);
  const globalGroupId                           = useSelector(globalGroupIdSelector);
  const globalGroupState                        = useSelector(globalGroupStateSelector);
  const filters                                 = useSelector(trackingFiltersSelector);
  const isAssetInitialized                      = useSelector(isFMAssetInitializedSelector);
  const isMapSelection                          = useSelector(isMapSelectionSelector);
  const isLabellingActive                       = useSelector(isLabellingActiveSelector);
  const isClusteringV2Active                    = useSelector(isClusteringV2ActiveSelector);
  const {filterApplied}                         = useSelector(trackingGridStateSelector);
  const findNearbyIsActive                      = useSelector(findNearbyV2IsActiveSelector);
  const isLiveHistoryActive                     = useSelector(liveHistoryActiveSelector);
  const displayLiveHistoryReport                = useSelector(liveHistoryDisplayReportSelector);
  const liveHistoryAssetsSearch                 = useSelector(liveHistoryAssetSearchListSelector);
  const trackingMode                            = useSelector(trackingModeSelector);
  const isAssetInitializedRef                   = useRef(isAssetInitialized);
  const selectedAssetRef                        = useRef(selectedAsset);
  const isFirstLoadRef                          = useRef(true);
  const liveHistoryAssetsSearchRef              = useRef(liveHistoryAssetsSearch);
  const currentAssetsRef                        = useRef<CVSAssetType[]>([]);
  const isSmoothAssetMovementActive             = useRef(false);
  const viewZoomWatchRef                        = useRef(null);
  const viewZoomLevelRef                        = useRef(view?.zoom);
  const viewExtentRef                           = useRef(view?.extent);
  const filteredIds                             = useFilteredAssetIds();
  const {isCurrent: isCurrentTheme}             = useThemeContext();
  
  useViewExtent(view as any);

  useImperativeHandle(ref, () => ({zoomToFit}));

  const isSmoothMovementLevel = zoomLevel => zoomLevel >= MIN_ZOOM_LEVEL_FOR_ASSET_ANIMATION;

  /**
   * Determines whether a given asset is located within the current map view extent.
   *
   * @param {Object} asset - The asset object containing latitude and longitude properties.
   * @returns {boolean} - Returns true if the asset is within the current view extent; otherwise, false.
   */
  /**
   * Splits a list of assets into two groups: those inside and outside the current map view extent.
   *
   * @param {Array<Object>} assets - An array of asset objects with latitude and longitude.
   * @returns {Object} - An object containing two arrays:
   *                     - assetsInExtent: assets within the map view extent
   *                     - assetsOutOfExtent: assets outside the map view extent
   */
  const groupAssetsByViewExtent = useCallback(assets => {
    const assetsInExtent    = [];
    const assetsOutOfExtent = [];

    if (assets?.length) {
      // the view extent, expanded 4x, is built once for the whole batch and
      // each asset is tested with plain numbers - no Point or extent clone
      // per asset ...
      const isAssetWithinExtent = createAssetExtentTest(viewExtentRef.current?.clone().expand(4));
      assets.forEach(asset => {
        isAssetWithinExtent(asset)
          ? assetsInExtent.push(asset)
          : assetsOutOfExtent.push(asset);
      });
    }
    return {assetsInExtent, assetsOutOfExtent};
  }, [viewExtentRef.current]);
  
  useEffect(() => {
    if (!view) return;
    isSmoothAssetMovementActive.current = isSmoothMovementLevel(view.zoom);
    // Watch for zoom level changes
    viewZoomWatchRef.current = view.watch('stationary', () => {
      if (view.stationary) {
        viewZoomLevelRef.current = view.zoom;
        viewExtentRef.current    = view.extent;
        isSmoothAssetMovementActive.current = isSmoothMovementLevel(view.zoom);
      }
    });
    // Clean up watcher on unmount
    return () => {
      viewZoomWatchRef?.current?.remove();
    };
  }, [view]);

  useEffect(() => {
    liveHistoryAssetsSearchRef.current = liveHistoryAssetsSearch;
  }, [liveHistoryAssetsSearch]);

  const setFleetAssetReady = useCallback((status: boolean) => {
    setLayerReady(status);
  }, []);

  const zoomToFit = useCallback(async() => {
    if (fleetAssetLayerRef.current) {
      const extent = await fleetAssetLayerRef.current?.getAssetExtent();
      if (extent) {
        view?.goTo(extent, mapViewGoToOptions);
        dispatch(setSelectedAsset({asset: null}) as any);
      }
    }
  }, [view, fleetAssetLayerRef]);

  const isTrackingBetaActive = useMemo(() =>
    isCurrentPage(route?.route, Route.TRACKING2) && trackingMode === TrackingMode.Live,
  [route?.route, trackingMode]);
  
  // the one reading of what is on the map, shared with the timeline so the
  // assets it reports over are always the ones drawn here ...
  const definitionExpression = useMemo(() => (
    getFleetDefinitionExpression(filters, filteredIds, filterApplied)
  ), [filters, filteredIds, filterApplied]);

  const featureFilter = useMemo(() => {
    let filter       = null;
    const {boundary} = filters;
    if (definitionExpression !== ALL_FEATURES_DEF) {
      filter = {
        where : definitionExpression
      };
    }
    if (boundary?.length) {
      const polygon = new Polygon({
        rings            : boundary as any,
        spatialReference : {wkid: 102100}
      });
      const boundaryFilter = {
        geometry            : polygon,
        spatialRelationship : 'contains'
      };
      if (!filter) {
        filter = boundaryFilter;
      } else {
        filter = {...filter, ...boundaryFilter};
      }
    };
    
    if (isLiveHistoryActive && liveHistoryAssetsSearchRef.current?.length) {
      const assetIds = getValuebyField(liveHistoryAssetsSearchRef.current, 'id');
      const query = toLiveHistoryExclusion(assetIds);
      if (filter) {
        filter.where += ` AND ${query}`;
      } else {
        filter = {
          where : query
        };
      }
    }
    return filter;
  }, [
    definitionExpression,
    filters.boundary,
    isLiveHistoryActive,
    displayLiveHistoryReport
  ]);

  const queryParams = useMemo(() => {
    let accountIds = null;
    if (!isNil(globalGroupId)) {
      accountIds = getAllAccountIds(globalGroupState, entityId(globalGroupId));
    };
    const params = {
      groups : accountIds
    };
    return {params};
  }, [globalGroupState, globalGroupId]);
  
  const {data: assetResults, isKeyInCache, hasNextPage, hydrateData} = useLoadAssets({
    queryParams,
    globalGroupId,
    baseKey     : QueryKey.LOAD_ASSETS,
    rowsPerPage : 5000,
    enabled     : isLayerReady
  });

  const setAssetState = useCallback(async(attributes: any[], globalGroupId: number) => {
    if (globalGroupId) {
      hydrateData({data: attributes, baseKey: QueryKey.LOAD_ASSETS, globalGroupId, queryParams});
    }
  }, [queryParams]);

  const onFindNearbyRefresh = useCallback((value: boolean = true) => {
    if (findNearbyIsActive) {
      dispatch(setFindNearbyRefresh?.(value));
    }
  }, [findNearbyIsActive]);

  useEffect(() => {
    currentAssetsRef.current = [];
  }, [globalGroupId]);

  useEffect(() => {
    if (!isKeyInCache(QueryKey.LOAD_ASSETS, globalGroupId, queryParams)) {
      dispatch(setFMAssetInitialized(false));
    }
  }, [globalGroupId, queryParams]);
  
  useEffect(() => {
    selectedAssetRef.current = selectedAsset;
  }, [selectedAsset]);

  useEffect(() => {
    if (isFalse(hasNextPage)) {
      dispatch(setFMAssetInitialized(!hasNextPage));
    }
  }, [hasNextPage]);

  useEffect(() => {
    isAssetInitializedRef.current = isAssetInitialized;
  }, [isAssetInitialized]);

  const assets = useMemo(() => {
    const {pages} = assetResults || {} as any;
    if (pages?.length && isLayerReady) {
      if (!isAssetInitializedRef.current && !isFirstLoadRef.current) {
        return pages[pages.length - 1];
      }
      isFirstLoadRef.current = false;
      const flattenedAssets  = pages?.flat();
      return flattenedAssets;
    };
    return [];
  }, [assetResults, isLayerReady]);

  const {
    data: assetUpdatesResults,
    refetch,
    isLoading,
    isFetching,
    isFetchingNextPage,
    status
  } = useLoadAssetUpdatesV2({
    queryParams,
    globalGroupId,
    currentAssets               : currentAssetsRef.current,
    parentBaseKey               : QueryKey.LOAD_ASSETS,
    baseKey                     : QueryKey.LOAD_ASSET_UPDATES,
    enabled                     : isFalse(hasNextPage) && isLayerEnabled,
    isSmoothAssetMovementActive : isSmoothAssetMovementActive.current,
    fnCallback                  : groupAssetsByViewExtent,
    rowsPerPage                 : 5000,
    // current theme: each refetch is new deltas - skip the deep comparison ...
    structuralSharing           : !isCurrentTheme
  });

  useTrackedFleetUpdate(
    'fleet',
    refetch,
    !(isFalse(hasNextPage) && isLayerEnabled && isTrackingBetaActive) ||
    (isLoading || isFetching || isFetchingNextPage || status === 'idle')
  );

  const assetDataUpdates = useMemo(() => {
    const {pages} = assetUpdatesResults || {} as any;
    if (pages?.length && isLayerReady && isAssetInitialized && isLayerEnabled) {
      return pages[pages.length - 1];
    };
    return {};
  }, [assetUpdatesResults, isLayerReady, isAssetInitialized]);

  const {
    assets: assetUpdates,
    assetRoutes: assetRouteUpdates
  } = assetDataUpdates || {} as any;

  const routeUpdatesRaw = useMemo(() => mergeNodesIntoRoutes(assetRouteUpdates), [assetRouteUpdates]);

  // defers the heavy map layer re-render these trigger once react-query's cache updates for
  // the 'fleet' auto-refresh cycle, since startTransition alone doesn't cover an async cache
  // update landing after the call that started it ...
  const deferredAssetUpdates = useDeferredValue(assetUpdates);
  const routeUpdates         = useDeferredValue(routeUpdatesRaw);

  useEffect(() => {
    if (assets?.length) {
      currentAssetsRef.current = getLastAVLEvent({data: assets, dataSaved: currentAssetsRef.current});
    }
  }, [assets]);
  
  useEffect(() => {
    if (assetUpdates?.length) {
      currentAssetsRef.current = getLastAVLEvent({data: assetUpdates, dataSaved: currentAssetsRef.current});
    }
  }, [assetUpdates]);

  useEffect(() => {
    if (assetUpdates?.length && selectedAssetRef.current) {
      dispatch(updateSelectedAsset({
        selectedAsset : selectedAssetRef.current,
        assets        : assetUpdates,
        panMapOnly    : true,
        tracking2Page : true
      }) as any);
    }
  }, [assetUpdates]);

  useEffect(() => {
    if (isFleetAssetLayerRendered) {
      if (fleetAssetLayerRef.current && mapSelectedAsset) {
        const asset = getItemValue(mapSelectedAsset);
        const assetNormalized = {...asset, isCvsAsset: true};
        setSelectedAssetMap(assetNormalized as any);
      } else {
        setSelectedAssetMap(null);
      }
    }
  }, [mapSelectedAsset, fleetAssetLayerRef, isFleetAssetLayerRendered]);

  const layerProps = useMemo(() => {
    return {
      selectedAsset               : selectedAssetMap,
      onZoomToClusterHandler      : zoomToClusterExtent,
      onShowPopup                 : showPopup,
      isClusteringActive          : isClusteringV2Active,
      viewZoomLevel               : viewZoomLevelRef.current,
      isSmoothAssetMovementActive : isSmoothAssetMovementActive.current,
      assets,
      layers,
      basemap,
      routeUpdates,
      assetUpdates                : deferredAssetUpdates,
      labelContent,
      setAssetState,
      assetsUpdates,
      globalGroupId,
      featureFilter,
      isMapSelection,
      setLayerEnabled,
      isLabellingActive,
      setFleetAssetReady,
      findNearbyIsActive,
      onFindNearbyRefresh,
      setFleetAssetLayerRendered
    };
  }, [
    assets,
    layers,
    basemap,
    showPopup,
    routeUpdates,
    deferredAssetUpdates,
    labelContent,
    setAssetState,
    assetsUpdates,
    globalGroupId,
    featureFilter,
    isMapSelection,
    setLayerEnabled,
    selectedAssetMap,
    isLabellingActive,
    setFleetAssetReady,
    findNearbyIsActive,
    zoomToClusterExtent,
    onFindNearbyRefresh,
    isClusteringV2Active,
    viewZoomLevelRef.current,
    setFleetAssetLayerRendered,
    isSmoothAssetMovementActive.current
  ]);

  if (isMounted) {
    return (
      <FleetAssetLayer ref={fleetAssetLayerRef}
        map={map}
        view={view}
        clusterColor={clusterColor}
        layerProps={layerProps} />
    );
  }
};

export default forwardRef(FleetLayerContainer);
